/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { HotToastService } from '@ngneat/hot-toast';
import { BadgeOptions } from 'app/components/badge/badge.interface';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { PickerSelectOptions } from '../data-picker.interface';
import _ from 'lodash';
import { TimezonePickerService } from 'app/core/services/timezone/timezone-picker.service';

@Component({
  selector: 'organization-data-picker',
  templateUrl: './organization-data-picker.component.html',
  styleUrls: ['./organization-data-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrganizationDataPickerComponent implements OnInit, OnChanges {
  @Input() filter: any = null;
  @Input() selectOption: PickerSelectOptions = PickerSelectOptions.select;
  @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
  @Input() selectedOrganizations: any[] = [];
  @Input() searchKey: string = '';
  @Input() pickerConfig: any = null;
  @Input() organizationUrl: any = '';
  @Input() pageIndex: number = 0;
  @Input() showSelectedItems: boolean = false;
  @Input() sortBy: string = 'organization_name';
  @Input() sortType: string = 'ASC';
  @Input() customParams: any = null;

  organizations: any[] = [];
  organizationsList: any[] = [];
  loaded: boolean = false;
  defaultParamObj: any = {
    'sort': this.sortType,
    'sort_by': this.sortBy,
    'filter.is_active': true
  };
  orgEleId: string;
  organizationDetails: any = null;
  onSearch: boolean = true;
  localeKeys: any = {
    expand: 'expand',
    collapse: 'collapse'
  };
  isSpinLoading: boolean = false;

  constructor(
    private utilityService: UtilityService,
    private dataCollectorService: DataCollectorService,
    private localisationService: LocalisationService,
    private changeDetectorRef: ChangeDetectorRef,
    private timezoneService: TimezonePickerService,
    private toast: HotToastService) { }

  ngOnInit(): void {
    this.localisationService.setDefaultLanguage();
    if (this.selectOption && this.selectOption === PickerSelectOptions.readOnly) {
      delete this.defaultParamObj?.['filter.is_active'];
    }
    this.showDataPickerPanel();
  }

  rotateArrowIcon(orgUID: any): any {
    return {
      'rotate-arrow-icon-down': this.isExpandingAction(orgUID),
      'rotate-arrow-icon-up': !this.isExpandingAction(orgUID)
    };
  }

  showSpinLoading(orgUID: any): boolean {
    return this.isExpandingAction(orgUID) && this.isSpinLoading;
  }

  updateArrowTooltipText(orgUID: any): string {
    return this.isExpandingAction(orgUID) ? this.localeKeys.collapse : this.localeKeys.expand;
  }

  isExpandingAction(orgUID: any): boolean {
    return (this.orgEleId === 'org_details_' + orgUID);
  }

  viewOrganizationFullDetails(orgUID: string): string {
    return '/organizations/' + orgUID + '/details';
  }

  getStatusBadgeOptions(type: string, displayName: string): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: type,
      displayType: type,
      displayKey: null,
      displayName: displayName ?? type
    };
    return badgeOption;
  }

  viewOrganizationDetails(event: any, orgUID: string): void {
    event.stopPropagation();
    const elementId: string = 'org_details_' + orgUID;
    if (this.orgEleId === elementId) {
      this.resetOrganizationDetails();
    } else {
      this.orgEleId = elementId;
      this.getOrganizationDetails(orgUID);
    }
  }

  getOrganizationDetails(orgUID: string): void {
    this.isSpinLoading = true;
    /* eslint-disable quotes */
    this.dataCollectorService.getData(this.organizationUrl + `/` + orgUID).subscribe(
      /* eslint-enable quotes */
      (organizations: any) => {
        this.organizationDetails = organizations.data;
        if(this.organizationDetails.organization_timezone){
          const tz: string = this.timezoneService.getSelectedTimeZone(this.organizationDetails.organization_timezone);
          this.organizationDetails = {...this.organizationDetails,organization_timezone_value: tz};
        }
        this.isSpinLoading = false;
        this.changeDetectorRef.detectChanges();
      }, (error: any) => {
        this.resetOrganizationDetails();
        this.isSpinLoading = false;
      });
  }

  resetOrganizationDetails(): void {
    this.orgEleId = '';
    this.organizationDetails = null;
  }

  isSelectedOrganization(organization: any): boolean {
    if (this.selectedOrganizations?.length) {
      return this.selectedOrganizations.find(value => value.organization_uid === organization.organization_uid) ? true : false;
    }
    return false;
  }

  selectOrganization(event: any, organization: any): void {
    if (this.selectOption === PickerSelectOptions.multiSelect) {
      if (event?.target?.checked === true) {
        this.selectedOrganizations.push(organization);
      } else {
        this.selectedOrganizations = this.selectedOrganizations.filter(value => value.organization_uid !== organization.organization_uid);
      }
    } else {
      this.selectedOrganizations = [organization];
    }
    this.eventEmitter.emit({ value: this.selectedOrganizations });
  }

  triggerSearch(searchKey: any): void {
    delete this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey];
    this.onSearch = true;
    if (this.defaultParamObj[this.pickerConfig.params.searchKey] !== searchKey) {
      const pageIndex: number = 0;
      this.defaultParamObj[this.pickerConfig.params.searchKey] = searchKey;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  sortingAction(key: string, value: string): void {
    if (value) {
      const pageIndex: number = this.pageIndex??0;
      this.defaultParamObj[key] = value;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  getData(pageIndex: number, isOpen?: boolean): void {
    this.orgEleId = '';
    this.loaded = false;
    this.defaultParamObj.page = pageIndex + 1;
    const urlParams = this.utilityService.constructParam(this.defaultParamObj);

    this.dataCollectorService.getData(this.organizationUrl, urlParams).subscribe(
      (response: any) => {
        this.organizationsList = response.data;
        this.organizations = response.data;
        this.loaded = true;
        this.eventEmitter.emit({ totalRecords: response.total_records });
        if (isOpen && this.filter && this.organizations.length) {
          this.selectedOrganizations = this.selectedOrganizations?.length > 0 ? _.uniqBy([...this.selectedOrganizations, ...this.filter], 'organization_uid') : this.filter;
          this.eventEmitter.emit({ value: this.selectedOrganizations });
        } else if (isOpen && this.organizations.length === 0) {
          this.onSearch = false;
        }
        this.changeDetectorRef.detectChanges();
      }, (error: any) => {
        this.loaded = true;
      });
  }

  showDataPickerContent(): void {
    const pageIndex: number = this.pageIndex??0;
    Object.keys(this.pickerConfig.params).forEach((key: any) => {
      const paramKey: any = this.pickerConfig.params[key];
      if (key !== 'uniqueSearchKey') {
        let value: any = '';
        if (key === 'countKey') {
          value = 10;
        } else if (key === 'pageKey') {
          value = 1;
        } else if (key === 'searchKey' && !this.showSelectedItems && this.searchKey) {
          value = this.searchKey;
        }
        this.defaultParamObj[paramKey] = value;
      }
    });
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    if (this.filter) {
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
    this.eventEmitter.emit({ sortBy: this.defaultParamObj['sort_by'], sortType: this.defaultParamObj['sort'] });
    this.getData(pageIndex, true);
  }

  resetOrganizationDataPicker(): void {
    this.organizations = [];
  }

  showDataPickerPanel(): void {
    this.resetOrganizationDataPicker();
    if (this.showSelectedItems) {
      this.organizations = this.selectedOrganizations;
    } else if (this.organizationsList.length === 0 && this.searchKey) {
      this.showDataPickerContent();
    } else if (this.organizationsList.length > 0 || this.searchKey) {
      this.organizations = this.organizationsList;
    } else {
      this.showDataPickerContent();
    }
  }

  hideSelectedItemsPanel(): void {
    this.eventEmitter.emit({ showSelectedItems: false, value: this.selectedOrganizations, filter: this.selectedOrganizations });
  }

  getBadgeOptions(): BadgeOptions {
    return {
      type: 'PRIMARY',
      displayKey: null,
      displayName: this.selectedOrganizations ? this.selectedOrganizations.length : 0,
      displayType: 'listingCount'
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.searchKey?.firstChange === false) {
      this.triggerSearch(changes?.searchKey?.currentValue);
    }

    if (changes?.pageIndex?.previousValue !== undefined) {
      this.getData(changes?.pageIndex?.currentValue);
    }

    if (changes?.showSelectedItems?.firstChange === false) {
      this.showDataPickerPanel();
    }

    if (changes?.sortBy?.firstChange === false && changes?.sortBy?.previousValue !== undefined) {
      this.sortingAction('sort_by', changes?.sortBy?.currentValue);
    }

    if (changes?.sortType?.firstChange === false && changes?.sortType?.previousValue !== undefined) {
      this.sortingAction('sort', changes?.sortType?.currentValue);
    }
  }
}
